import type { UserCreditsStatus } from "@softmax/observatory-client/types";

// Meter severity for experience credits, shared by the account page, the XP-request
// list, and the request form so the thresholds and wording cannot drift apart.
type CreditsLevel = "over" | "low" | "ok";
// "Low" = under 15% of the balance cap (150 credits for users, 3,000 for team).
// Anchored to the cap, not the refill, so the warning band is independent of the
// refill schedule's shape — the weekly-to-daily drip change divided refill_credits
// by 7 but left the cap (and this band) unchanged.
const lowBalanceFractionOfMax = 0.15;

export function creditsLevel(status: UserCreditsStatus): CreditsLevel {
  // Half-credit epsilon: a balance that rounds to 0 reads as "low", never as
  // "0 credits past your allowance".
  if (status.balance_credits < -0.5) return "over";

  if (
    status.balance_credits <
    lowBalanceFractionOfMax * status.max_balance_credits
  ) {
    return "low";
  }

  return "ok";
}

// A grant or prize can hold the balance above the cap (refills never claw it
// back); "{balance} of {cap}" would read as a bug there, so callers branch.
export function isAboveCap(status: UserCreditsStatus): boolean {
  return status.balance_credits > status.max_balance_credits + 0.5;
}

// The one explanation of the above-cap state, shared verbatim by every surface
// (see the header comment). Lowercase so it can follow an em-dash; a standalone
// rendering capitalizes it.
function aboveCapExplanation(status: UserCreditsStatus): string {
  return `a grant or prize holds your balance above the ${formatCredits(status.max_balance_credits)}-credit cap, so refills pause until spend brings it back under`;
}

export function formatCredits(credits: number): string {
  // + 0 normalizes -0 (e.g. Math.round(-0.4)) so it never renders as "-0".
  return (Math.round(credits) + 0).toLocaleString();
}

// The refill instant is a UTC midnight. With a daily period, a date-only
// rendering says "next on <today's date>" for most of the day anywhere west of
// UTC — reads as "already happened?". Relative time is the only rendering that
// is unambiguous in every timezone. `now` is injectable for tests only.
export function formatNextRefill({
  nextRefillAt,
  now = new Date(),
}: {
  nextRefillAt: string;
  now?: Date | undefined;
}): string {
  const minutes = Math.round(
    (new Date(nextRefillAt).getTime() - now.getTime()) / 60000,
  );

  if (minutes < 60) return `in ${Math.max(minutes, 1)}m`;

  if (minutes < 48 * 60) return `in ${Math.round(minutes / 60)}h`;

  return `in ${Math.round(minutes / (24 * 60))}d`;
}

export function creditsStatusLine(status: UserCreditsStatus): {
  text: string;
  level: CreditsLevel;
} {
  const refill = `+${formatCredits(status.refill_credits)} free credits ${formatNextRefill({ nextRefillAt: status.next_refill_at })}`;
  const notEnforced = status.enforced ? "" : " (not enforced for this account)";
  const level = creditsLevel(status);

  if (level === "over") {
    const over = formatCredits(-status.balance_credits);
    return {
      text: `${over} credits past your allowance — requests are denied until refills catch up. ${refill}${notEnforced}`,
      level,
    };
  }

  if (isAboveCap(status)) {
    return {
      text: `${formatCredits(status.balance_credits)} credits available — ${aboveCapExplanation(status)}${notEnforced}`,
      level,
    };
  }

  const balance = `${formatCredits(status.balance_credits)} of ${formatCredits(status.max_balance_credits)} credits`;

  if (level === "low") {
    return {
      text: `Running low: ${balance} left — ${refill}${notEnforced}`,
      level,
    };
  }

  return { text: `${balance} available — ${refill}${notEnforced}`, level };
}
